import { buildAIPrompt, SYMPTOM_NAMES } from './aiPromptTemplate';

/**
 * Build context summary from immunity survey answers
 */
export function buildImmunityContext(answers) {
    const contextParts = [];

    // Safety check
    if (answers.safety) {
        if (answers.safety.includes('none')) {
            contextParts.push('- Good health condition, no specific risks');
        } else {
            contextParts.push('- Some health concerns to note');
        }
    }

    // Demographics
    if (answers.demographics) {
        const { age, weight } = answers.demographics;
        if (age) contextParts.push(`- Age group: ${age}`);
        if (weight) contextParts.push(`- Weight: ${weight}`);
    }

    // Main reason
    if (answers.mainReason) {
        const reasonMap = {
            'frequent_cold': 'Frequent colds',
            'fatigue': 'Always feeling fatigued',
            'seasonal': 'Sick during season changes',
            'prevention': 'Preventive constitution boosting'
        };
        contextParts.push(`- Main reason: ${reasonMap[answers.mainReason]}`);
    }

    // 分支A：经常感冒
    if (answers.coldFrequency) {
        const freqMap = {
            '1-2': '1-2 times a year',
            '3-4': '3-4 times a year',
            '5+': '5+ times a year'
        };
        contextParts.push(`- Cold frequency: ${freqMap[answers.coldFrequency]}`);
    }

    if (answers.coldSymptoms && answers.coldSymptoms.length > 0) {
        const symptoms = answers.coldSymptoms.map(s => {
            switch (s) {
                case 'sweating': return 'Easily sweating';
                case 'cold_limbs': return 'Fear of cold/cold hands and feet';
                case 'runny_nose': return 'Runny nose';
                case 'sore_throat': return 'Sore throat/fever';
                default: return s;
            }
        });
        contextParts.push(`- Cold symptoms: ${symptoms.join(', ')}`);
    }

    if (answers.recoveryTime) {
        const recoveryMap = {
            '3-5days': 'Recover in 3-5 days',
            '1week': 'Recover in about a week',
            '>10days': 'Slow recovery (>10 days)'
        };
        contextParts.push(`- ${recoveryMap[answers.recoveryTime]}`);
    }

    // 分支B：疲劳
    if (answers.fatigueType && answers.fatigueType.length > 0) {
        const types = answers.fatigueType.map(t => {
            switch (t) {
                case 'physical': return 'Physical weakness';
                case 'mental': return 'Mental fatigue';
                case 'chronic': return 'Tired even after sleep';
                default: return t;
            }
        });
        contextParts.push(`- Fatigue type: ${types.join(', ')}`);
    }

    if (answers.fatigueDuration) {
        const durationMap = {
            '1-2months': 'Fatigue for 1-2 months',
            '3-6months': 'Fatigue for 3-6 months',
            '>6months': 'Fatigue for over 6 months'
        };
        contextParts.push(`- ${durationMap[answers.fatigueDuration]}`);
    }

    if (answers.sleepQuality) {
        const sleepMap = {
            'poor': 'Poor sleep',
            'normal': 'Normal sleep',
            'excessive': 'Sleep a lot but still tired'
        };
        contextParts.push(`- Sleep: ${sleepMap[answers.sleepQuality]}`);
    }

    // 分支C：换季
    if (answers.seasonalTime && answers.seasonalTime.length > 0) {
        const seasons = answers.seasonalTime.map(s => {
            switch (s) {
                case 'spring': return 'Spring';
                case 'autumn_winter': return 'Autumn/Winter';
                case 'summer': return 'Summer';
                default: return s;
            }
        });
        contextParts.push(`- Seasons prone to sickness: ${seasons.join(', ')}`);
    }

    if (answers.seasonalSymptoms && answers.seasonalSymptoms.length > 0) {
        const symptoms = answers.seasonalSymptoms.map(s => {
            switch (s) {
                case 'sneeze': return 'Sneezing/runny nose';
                case 'cold_fever': return 'Cold/fever';
                case 'fatigue': return 'Fatigue/low energy';
                case 'digestive': return 'Digestive discomfort';
                default: return s;
            }
        });
        contextParts.push(`- Seasonal symptoms: ${symptoms.join(', ')}`);
    }

    // 分支D：预防
    if (answers.preventionGoal && answers.preventionGoal.length > 0) {
        const goals = answers.preventionGoal.map(g => {
            switch (g) {
                case 'prevent_cold': return 'Prevent colds';
                case 'boost_energy': return 'Boost energy';
                case 'stress': return 'Handle stress';
                case 'exercise': return 'Support exercise';
                default: return g;
            }
        });
        contextParts.push(`- Prevention goals: ${goals.join(', ')}`);
    }

    // Constitution
    if (answers.constitution && answers.constitution.length > 0) {
        const constitution = answers.constitution
            .filter(c => c !== 'none')
            .map(c => {
                switch (c) {
                    case 'night_sweat': return 'Night sweats';
                    case 'dry_mouth': return 'Dry mouth';
                    case 'cold_limbs': return 'Cold hands and feet';
                    case 'heat_intolerance': return 'Heat intolerance/inflammation';
                    default: return c;
                }
            });
        if (constitution.length > 0) {
            contextParts.push(`- Constitution: ${constitution.join(', ')}`);
        }
    }

    // Stress
    if (answers.stress) {
        const stressMap = {
            'high': 'High stress',
            'moderate': 'Moderate stress',
            'low': 'Low stress'
        };
        contextParts.push(`- Stress level: ${stressMap[answers.stress]}`);
    }

    // Other symptoms
    if (answers.otherSymptoms && answers.otherSymptoms.length > 0) {
        const symptoms = answers.otherSymptoms
            .filter(s => s !== 'none')
            .map(s => {
                switch (s) {
                    case 'loose_stool': return 'Loose stools';
                    case 'poor_appetite': return 'Poor appetite';
                    case 'shortness_breath': return 'Shortness of breath';
                    default: return s;
                }
            });
        if (symptoms.length > 0) {
            contextParts.push(`- Other symptoms: ${symptoms.join(', ')}`);
        }
    }

    return contextParts.join('\n');
}

/**
 * Build AI prompt for immunity questions
 */
export function buildImmunityPrompt(question, answers, userName) {
    const context = buildImmunityContext(answers);
  return buildAIPrompt(SYMPTOM_NAMES.immunity, context, question.text, userName);
}